import { html, nothing, svg } from '../application_element/element.js';
import PlotElement from '../plot_element/element.js';
import style from './style.lit.css';
import translations from './translations.js';

/** Above this the dots crowd the line and stop being readable as points. */
const MAX_MARKERS = 14;

const round = value => Number(value.toFixed(3));

/**
 * A timeseries as a line, optionally filled underneath.
 *
 * Only the line itself is SVG, stretched to the plot with `preserveAspectRatio`.
 * Everything with text or a fixed size — axis, labels, point markers — is HTML,
 * so nothing is distorted by the stretch and nothing has to be measured first.
 *
 * @prop area - Fill the space under the line ( boolean, default: false )
 *
 * @cssprop [--line-width: 2px] - Stroke width, held constant as the plot stretches.
 * @cssprop [--area-opacity: 0.12] - Opacity of the fill under the line.
 */
export default class LineChart extends PlotElement {
  static properties = {
    area: { type: Boolean },
  };

  static translations = translations;

  static styles = [PlotElement.styles, style];

  constructor() {
    super();
    this.area = false;
  }

  get color() {
    return this.colors[0];
  }

  get markers() {
    return this.rows.length <= MAX_MARKERS;
  }

  get linePath() {
    return this.rows
      .map(
        (row, index) =>
          `${index === 0 ? 'M' : 'L'} ${this.xOf(index)} ${this.yOf(row)}`
      )
      .join(' ');
  }

  /** The line, then down to the baseline and back, so the fill closes cleanly. */
  get areaPath() {
    const last = this.rows.length - 1;

    return `${this.linePath} L ${this.xOf(last)} 100 L ${this.xOf(0)} 100 Z`;
  }

  /**
   * Centred on its bucket rather than spanning edge to edge. A bucket is a
   * period, not an instant, and this is also what puts a point directly above
   * its label — the label row divides the same width into the same n slots.
   */
  xOf(index) {
    return round(((index + 0.5) / this.rows.length) * 100);
  }

  yOf(row) {
    return round(100 - this.heightOf(row));
  }

  renderMarker(row, index) {
    return html`
      <span
        class="marker"
        style="left: ${this.xOf(index)}%; bottom: ${this.heightOf(
          row
        )}%; border-color: ${this.color}"
        title=${this.descriptionOf(row)}></span>
    `;
  }

  render() {
    if (this.empty) return this.renderEmpty();

    return this.renderPlot(html`
      <div class="body series">
        <!-- Stretched to the plot: the path has no text and no fixed size, and
             the stroke is held at its own width by vector-effect. -->
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true">
          ${this.area
            ? svg`
                <path
                  class="area"
                  d=${this.areaPath}
                  style="fill: ${this.color}"></path>
              `
            : nothing}
          ${this.rows.length > 1
            ? svg`
                <path
                  class="stroke"
                  d=${this.linePath}
                  style="stroke: ${this.color}"
                  vector-effect="non-scaling-stroke"></path>
              `
            : nothing}
        </svg>
        ${this.markers
          ? this.rows.map((row, index) => this.renderMarker(row, index))
          : nothing}
      </div>
    `);
  }
}

LineChart.extendTranslationsAs('xs-line-chart');

customElements.get('xs-line-chart') ||
  customElements.define('xs-line-chart', LineChart);
